import React, { useState, useEffect, useRef, useMemo } from 'react'
import { KTSVG } from '../../../../_metronic/helpers'
import { Search } from 'lucide-react'
import MovementReport from './MovementReport'
import DailyFuelData from './DailyFuelData'
import FuelConsumptionReport from './FuelConsumptionReport'
import VesselStatus, {blankVesselStatusPositionFields} from './VesselStatus'
import { toast, ToastContainer } from 'react-toastify'
//import { toast, ToastContainer } from 'react-toastify'
import { getDmrList, createDmr } from '../core/_requests'
import { useAuth } from '../../auth'
import { OP } from '../core/operationPermissions'
import { useOperationsAccess } from '../core/useOperationsAccess'
import { blankDailyMovementReport, DailyMovementReportRecord, DmrMovementReportRow } from '../core/_dmrModels'
import { Modal } from 'react-bootstrap'
import VesselLandingLink from '../../../shared/components/VesselLandingLink'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { fetchAccessibleVesselsForCurrentUser, normalizeAccessibleVessels } from '../../../shared/access/accessibleVessels'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import { getApiErrorMessage } from '../../../shared/utils/apiErrorMessage'
import { TableSkeleton } from '../../../shared/components/LoadingSkeletons'

type TabKey = 'vessel-status' | 'daily-fuel-data' | 'fuel-consumption-report' | 'movement-report'

const TABS: { key: TabKey; label: string }[] = [
  { key: 'vessel-status', label: 'Vessel Status' },
  { key: 'daily-fuel-data', label: 'Daily Fuel Data' },
  // { key: 'fuel-consumption-report', label: 'Fuel Consumption Report' },
  { key: 'movement-report', label: 'Movement Report' },
]

const formatDateDisplay = (dateStr: string) => {
  if (!dateStr) return '-'
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return dateStr
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  return `${day}/${month}/${year}`
}

const getPreviousFuelReport = (records: any[], vesselName?: string, vesselId?: number) => {
  if ((!vesselName || vesselName.trim() === '') && (vesselId === undefined || vesselId === null)) return null

  const toSortTime = (record: any) => {
    const createdTime = record?.createdDateTime ? new Date(record.createdDateTime).getTime() : NaN
    if (!Number.isNaN(createdTime)) return createdTime

    const reportTime = record?.reportDate ? new Date(record.reportDate).getTime() : NaN
    if (!Number.isNaN(reportTime)) return reportTime

    return 0
  }

  return records
    .filter((record) => {
      if (!record) return false
      if (vesselId && record.vesselId === vesselId) return true
      return record.vesselName === vesselName
    })
    .sort((left, right) => toSortTime(right) - toSortTime(left))[0] ?? null
}

/* ──────────────────── View DMR Modal ──────────────────── */
const ViewDmrModal: React.FC<{ isOpen: boolean; onClose: () => void; dmr: any }> = ({ isOpen, onClose, dmr }) => {
  if (!isOpen || !dmr) return null

  const movementRows: DmrMovementReportRow[] = dmr.movementReportRows || []
  const hasMovementData = movementRows.length > 0

  const totalHrs = movementRows.reduce((acc: number, r: DmrMovementReportRow) => {
    const val = parseFloat(r.totalRHrs || '0')
    return acc + (isNaN(val) ? 0 : val)
  }, 0)

  const fuelRows = (dmr.fuelRows || []).filter((r: any) => r.engine && r.engine.trim() !== '')
  const hasFuelData = fuelRows.length > 0

  const lubeRows = (dmr.lubeRows || dmr.lubeOilRows || dmr.lubeOilStatusRows || []).filter((r: any) => (r.grades && r.grades.trim() !== '') || (r.machinery && r.machinery.trim() !== ''))
  const hasLubeData = lubeRows.length > 0

  const fuelConsumptionRows = dmr.fuelConsumptionRows || []
  const hasFuelConsumptionData = fuelConsumptionRows.length > 0

  const fcrTotals = fuelConsumptionRows.reduce((acc: any, row: any) => {
    return {
      pmeRun: acc.pmeRun + (parseFloat(row.pmeRun) || 0),
      smeRun: acc.smeRun + (parseFloat(row.smeRun) || 0),
      fuelConsByMEs: acc.fuelConsByMEs + (parseFloat(row.fuelConsByMEs) || 0),
      portGenRun: acc.portGenRun + (parseFloat(row.portGenRun) || 0),
      stbdGenRun: acc.stbdGenRun + (parseFloat(row.stbdGenRun) || 0),
      fuelConsByGens: acc.fuelConsByGens + (parseFloat(row.fuelConsByGens) || 0),
      robOpeningLtr: acc.robOpeningLtr + (parseFloat(row.robOpeningLtr) || 0),
      transfer: acc.transfer + (parseFloat(row.transfer) || 0),
      received: acc.received + (parseFloat(row.received) || 0),
      consumption: acc.consumption + (parseFloat(row.consumption) || 0),
      robClosingLtr: acc.robClosingLtr + (parseFloat(row.robClosingLtr) || 0),
    }
  }, {
    pmeRun: 0, smeRun: 0, fuelConsByMEs: 0, portGenRun: 0, stbdGenRun: 0, fuelConsByGens: 0,
    robOpeningLtr: 0, transfer: 0, received: 0, consumption: 0, robClosingLtr: 0
  })

  const renderCellValue = (val: string | number) => {
    if (val === null || val === undefined || val === '') return '-'
    const strVal = String(val)
    if (strVal === '-') return '-'
    if (strVal.toLowerCase() === 'continue') {
      return <span className='badge badge-light-danger fs-8 fw-bolder'>Continue</span>
    }
    return strVal
  }

  const renderFcrTotal = (val: number) => {
    return val === 0 ? '0' : val.toFixed(1).replace(/\.0$/, '')
  }

  return (
    <Modal show={isOpen} onHide={onClose} size='xl' centered dialogClassName='custom-wide-modal' backdrop='static'>
      <style>{`
        .custom-wide-modal { max-width: 95vw !important; }
        @media print {
          .no-print { display: none !important; }
          .modal { position: static !important; }
          .modal-dialog { max-width: 100% !important; margin: 0 !important; }
          .modal-content { border: none !important; box-shadow: none !important; }
        }
      `}</style>
      <div className='modal-header'>
        <h2 className='fw-bolder'>Coastal Position Report — {dmr.vesselName || 'N/A'}</h2>
        <div className='btn btn-icon btn-sm btn-active-icon-primary no-print' onClick={onClose}>
          <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
        </div>
      </div>

      <div className='modal-body scroll-y mx-5 mx-xl-10 my-5'>
        {/* Report Info */}
        <div className='row mb-6'>
          <div className='col-md-3'>
            <label className='form-label fw-bolder text-muted fs-7'>Vessel</label>
            <div className='fw-bold fs-6'>{dmr.vesselName || '-'}</div>
          </div>
          <div className='col-md-3'>
            <label className='form-label fw-bolder text-muted fs-7'>Report Date</label>
            <div className='fw-bold fs-6'>{formatDateDisplay(dmr.reportDate)}</div>
          </div>
          <div className='col-md-3'>
            <label className='form-label fw-bolder text-muted fs-7'>Status</label>
            <div>
              <span className={`badge badge-light-${dmr.status === 'SUBMITTED' ? 'success' : 'warning'} fs-7`}>
                {dmr.status}
              </span>
            </div>
          </div>
          <div className='col-md-2'>
              <label className='form-label fw-bolder text-muted fs-7'>Vessel Status</label>
              <div className='fw-bold fs-6'>{dmr.vesselStatus || '-'}</div>
            </div>
            <div className='col-md-1'>
              <label className='form-label fw-bolder text-muted fs-7'>Off-Hire</label>
              <div className='fw-bold fs-6'>
                {dmr.isOffHire ? <span className='badge badge-light-danger'>Yes</span> : <span className='badge badge-light-success'>No</span>}
              </div>
            </div>
        </div>

        <div className='row mb-6'>
          <div className='col-md-6'>
            <label className='form-label fw-bolder text-muted fs-7'>Position / Location</label>
            <div className='fw-bold fs-6'>{dmr.positionLocation || '-'}</div>
          </div>
            <div className='col-md-6'>
              <label className='form-label fw-bolder text-muted fs-7'>Time</label>
            <div className='fw-bold fs-6'>{dmr.positionTime || '-'}</div>
          </div>
        </div>

        <div className='row mb-6'>
          <div className='col-12'>
            <label className='form-label fw-bolder text-muted fs-7'>Remarks</label>
            <div className='fw-bold fs-6'>{dmr.dfdRemarks || '-'}</div>
          </div>
        </div>

        <div className='separator my-5'></div>

        {/* Daily Fuel Data */}
        <h4 className='fw-bold mb-4'>Daily Fuel Data</h4>
        <div className='row mb-4'>
          <div className='col-md-2 mb-3'>
            <label className='form-label fw-bolder text-muted fs-7'>Opening Stock</label>
            <div className='fw-bold fs-6'>{renderCellValue(dmr.dfdOpeningStock)}</div>
          </div>
          <div className='col-md-2 mb-3'>
            <label className='form-label fw-bolder text-muted fs-7'>Total Consumption</label>
            <div className='fw-bold fs-6'>{renderCellValue(dmr.dfdTotalConsumption)}</div>
          </div>
          <div className='col-md-2 mb-3'>
            <label className='form-label fw-bolder text-muted fs-7'>Last Running Hrs</label>
            <div className='fw-bold fs-6'>{renderCellValue(dmr.dfdLastRunningHrs || dmr.lastRunningHrs)}</div>
          </div>
          <div className='col-md-2 mb-3'>
            <label className='form-label fw-bolder text-muted fs-7'>Total Running Hrs</label>
            <div className='fw-bold fs-6'>{renderCellValue(dmr.dfdTotalRunningHrs || dmr.totalRunningHrs)}</div>
          </div>
          <div className='col-md-2 mb-3'>
            <label className='form-label fw-bolder text-muted fs-7'>Bunker</label>
            <div className='fw-bold fs-6'>{renderCellValue(dmr.dfdBunker)}</div>
          </div>
          <div className='col-md-2 mb-3'>
            <label className='form-label fw-bolder text-muted fs-7'>Closing Stock</label>
            <div className='fw-bold fs-6'>{renderCellValue(dmr.dfdClosingStock)}</div>
          </div>
        </div>

        <h6 className='fw-bolder mt-6 mb-3'>Fuel Consumption Details</h6>
          <div className='table-responsive mb-5'>
            <table className='table table-bordered table-sm align-middle text-center mb-0' style={{ fontSize: '12px' }}>
              <thead className='table-light'>
                <tr className='fw-bolder text-muted'>
                  <th style={{width: '60px'}}>Sr</th>
                  <th>Engine</th>
                  <th>Consumption (KL)</th>
                  <th>Last Running Hours</th>
                  <th>Opening Running Hours</th>
                  <th>Closing Running Hours</th>
                  <th>Average</th>
                </tr>
              </thead>
              <tbody>
                {!hasFuelData ? (
                  <tr>
                    <td colSpan={7} className='text-center py-4 text-muted'>No fuel data recorded</td>
                  </tr>
                ) : (
                  fuelRows.map((row: any, idx: number) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td className='fw-bolder text-start ps-4'>{row.engine}</td>
                      <td>{renderCellValue(row.consumption)}</td>
                      <td>{renderCellValue(row.lastRunningHours)}</td>
                      <td>{renderCellValue(row.openingRunningHours)}</td>
                      <td>{renderCellValue(row.closingRunningHours)}</td>
                      <td>{renderCellValue(row.average)}</td>
                    </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <h6 className='fw-bolder mt-6 mb-3'>Lube Oil Status</h6>
        <div className='table-responsive mb-5'>
          <table className='table table-bordered table-sm align-middle text-center mb-0' style={{ fontSize: '12px' }}>
            <thead className='table-light'>
              <tr className='fw-bolder text-muted'>
                <th style={{width: '60px'}}>Sr. No</th>
                <th>Grades</th>
                <th>Opening Stock</th>
                <th>Received</th>
                <th>Consumed by Vessel</th>
                <th>Closing Stock</th>
              </tr>
            </thead>
            <tbody>
              {!hasLubeData ? (
                <tr>
                  <td colSpan={6} className='text-center py-4 text-muted'>No lube oil data recorded</td>
                </tr>
              ) : (
                lubeRows.map((row: any, idx: number) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td className='fw-bolder text-start ps-4'>{row.grades || row.machinery}</td>
                    <td>{renderCellValue(row.openingStock)}</td>
                    <td>{renderCellValue(row.received)}</td>
                    <td>{renderCellValue(row.consumedByVessel)}</td>
                    <td>{renderCellValue(row.closingStock)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className='separator my-5'></div>

        {false && (
        <>
        {/* Fuel Consumption Report */}
        <h4 className='fw-bold mb-4'>Fuel Consumption Report</h4>
        <div className='table-responsive mb-5'>
          <table className='table table-bordered table-sm align-middle text-center mb-0' style={{ fontSize: '12px' }}>
            <thead className='table-light'>
              <tr className='fw-bolder text-muted'>
                <th rowSpan={2} className='align-middle'>DATE</th>
                <th colSpan={3}>PORT MAIN ENGINE</th>
                <th colSpan={3}>STBD MAIN ENGINE</th>
                <th rowSpan={2} className='align-middle'>FUEL CONS BY M.E'S (LTRS)</th>
                <th colSpan={2}>GENERATOR ENGINE</th>
                <th rowSpan={2} className='align-middle'>FUEL CONS BY GEN'S (LTRS)</th>
                <th rowSpan={2} className='align-middle'>ROB OPENING (LTRS)</th>
                <th rowSpan={2} className='align-middle'>TRANSFER (LTRS)</th>
                <th rowSpan={2} className='align-middle'>RECEIVED (LTRS)</th>
                <th rowSpan={2} className='align-middle'>CONSUMPTION (LTRS)</th>
                <th rowSpan={2} className='align-middle'>ROB CLOSING (LTRS)</th>
              </tr>
              <tr className='fw-bolder text-muted'>
                <th>OPENING (HRS)</th>
                <th>CLOSING (HRS)</th>
                <th>RUN (HRS)</th>
                <th>OPENING (HRS)</th>
                <th>CLOSING (HRS)</th>
                <th>RUN (HRS)</th>
                <th>PORT (HRS)</th>
                <th>STBD (HRS)</th>
              </tr>
            </thead>
            <tbody>
              {!hasFuelConsumptionData ? (
                <tr>
                  <td colSpan={16} className='text-center py-4 text-muted'>No fuel consumption data recorded</td>
                </tr>
              ) : (
                fuelConsumptionRows.map((row: any, idx: number) => (
                  <tr key={idx}>
                    <td>{renderCellValue(row.date)}</td>
                    <td>{renderCellValue(row.portMEOpening)}</td>
                    <td>{renderCellValue(row.portMEClosing)}</td>
                    <td>{renderCellValue(row.pmeRun)}</td>
                    <td>{renderCellValue(row.stbdMEOpening)}</td>
                    <td>{renderCellValue(row.stbdMEClosing)}</td>
                    <td>{renderCellValue(row.smeRun)}</td>
                    <td>{renderCellValue(row.fuelConsByMEs)}</td>
                    <td>{renderCellValue(row.portGenRun)}</td>
                    <td>{renderCellValue(row.stbdGenRun)}</td>
                    <td>{renderCellValue(row.fuelConsByGens)}</td>
                    <td>{renderCellValue(row.robOpeningLtr)}</td>
                    <td>{renderCellValue(row.transfer)}</td>
                    <td>{renderCellValue(row.received)}</td>
                    <td>{renderCellValue(row.consumption)}</td>
                    <td>{renderCellValue(row.robClosingLtr)}</td>
                  </tr>
                ))
              )}
            </tbody>
            {hasFuelConsumptionData && (
              <tfoot>
                <tr className='fw-bolder text-primary bg-light'>
                  <td colSpan={3} className='text-end pe-3'>TOTAL</td>
                  <td>{renderFcrTotal(fcrTotals.pmeRun)}</td>
                  <td colSpan={2}></td>
                  <td>{renderFcrTotal(fcrTotals.smeRun)}</td>
                  <td>{renderFcrTotal(fcrTotals.fuelConsByMEs)}</td>
                  <td>{renderFcrTotal(fcrTotals.portGenRun)}</td>
                  <td>{renderFcrTotal(fcrTotals.stbdGenRun)}</td>
                  <td></td>
                  <td></td>
                  <td>{renderFcrTotal(fcrTotals.transfer)}</td>
                  <td>{renderFcrTotal(fcrTotals.received)}</td>
                  <td>{renderFcrTotal(fcrTotals.consumption)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        </>
        )}

        <div className='separator my-5'></div>

        {/* Movement Report Table */}
        <h4 className='fw-bold mb-4'>Movement Report</h4>
        <div className='table-responsive'>
          <table className='table table-bordered table-sm align-middle text-center mb-0' style={{ fontSize: '12px' }}>
            <thead className='table-light'>
              <tr className='fw-bolder text-muted'>
                <th>BER/U.BER/SHIFTING</th>
                <th>PILOT NAME</th>
                <th>STARTED BOTH M.E.</th>
                <th>C/OFF</th>
                <th>MAKE FAST /PUSH START</th>
                <th>LINE CAST OFF/PUSH STOP</th>
                <th>PILOT RELEASE</th>
                <th>ALONG SIDE/ FINISHED M.E.</th>
                <th>TOTAL R/HRS</th>
                <th>OTHER TUG</th>
                <th>REMARKS</th>
              </tr>
            </thead>
            <tbody>
              {!hasMovementData ? (
                <tr>
                  <td colSpan={11} className='text-center py-4 text-muted'>No movement data recorded</td>
                </tr>
              ) : (
                movementRows.map((row: DmrMovementReportRow, idx: number) => (
                  <tr key={idx}>
                    <td>{renderCellValue(row.berShifting)}</td>
                    <td>{renderCellValue(row.pilotName)}</td>
                    <td>{renderCellValue(row.startedBothME)}</td>
                    <td>{renderCellValue(row.cOff)}</td>
                    <td>{renderCellValue(row.makeFastPushStart)}</td>
                    <td>{renderCellValue(row.lineCastOffPushStop)}</td>
                    <td>{renderCellValue(row.pilotRelease)}</td>
                    <td>{renderCellValue(row.alongSideFinishedME)}</td>
                    <td className='fw-bold'>{row.totalRHrs || '-'}</td>
                    <td>{renderCellValue(row.otherTug)}</td>
                    <td className='text-start'>{renderCellValue(row.remarks)}</td>
                  </tr>
                ))
              )}
            </tbody>
            {hasMovementData && (
              <tfoot>
                <tr className='fw-bolder bg-light'>
                  <td colSpan={8} className='text-end pe-3'>Total Hours</td>
                  <td className='text-primary'>{totalHrs.toFixed(2)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <div className='modal-footer no-print'>
        <button type='button' className='btn btn-light' onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  )
}

/* ──────────────────── Download Helper ──────────────────── */
const downloadDmrAsPdf = (dmr: any) => {
  const renderVal = (v: string | number) => {
    if (v === null || v === undefined || v === '') return '-'
    const strVal = String(v)
    return strVal === '-' ? '-' : strVal
  }

  const renderFcrTotal = (val: number) => {
    return val === 0 ? '0' : val.toFixed(1).replace(/\.0$/, '')
  }

  const movementRows: DmrMovementReportRow[] = dmr.movementReportRows || []
  const totalHrs = movementRows.reduce((acc: number, r: DmrMovementReportRow) => {
    const val = parseFloat(r.totalRHrs || '0')
    return acc + (isNaN(val) ? 0 : val)
  }, 0)

  const fuelRows = (dmr.fuelRows || []).filter((r: any) => r.engine && r.engine.trim() !== '')
  const lubeRows = (dmr.lubeRows || dmr.lubeOilRows || dmr.lubeOilStatusRows || []).filter((r: any) => (r.grades && r.grades.trim() !== '') || (r.machinery && r.machinery.trim() !== ''))
  const fuelConsumptionRows = dmr.fuelConsumptionRows || []

  const fcrTotals = fuelConsumptionRows.reduce((acc: any, row: any) => {
    return {
      pmeRun: acc.pmeRun + (parseFloat(row.pmeRun) || 0),
      smeRun: acc.smeRun + (parseFloat(row.smeRun) || 0),
      fuelConsByMEs: acc.fuelConsByMEs + (parseFloat(row.fuelConsByMEs) || 0),
      portGenRun: acc.portGenRun + (parseFloat(row.portGenRun) || 0),
      stbdGenRun: acc.stbdGenRun + (parseFloat(row.stbdGenRun) || 0),
      fuelConsByGens: acc.fuelConsByGens + (parseFloat(row.fuelConsByGens) || 0),
      robOpeningLtr: acc.robOpeningLtr + (parseFloat(row.robOpeningLtr) || 0),
      transfer: acc.transfer + (parseFloat(row.transfer) || 0),
      received: acc.received + (parseFloat(row.received) || 0),
      consumption: acc.consumption + (parseFloat(row.consumption) || 0),
      robClosingLtr: acc.robClosingLtr + (parseFloat(row.robClosingLtr) || 0),
    }
  }, {
    pmeRun: 0, smeRun: 0, fuelConsByMEs: 0, portGenRun: 0, stbdGenRun: 0, fuelConsByGens: 0,
    robOpeningLtr: 0, transfer: 0, received: 0, consumption: 0, robClosingLtr: 0
  })

  const movementRowsHtml = movementRows.length > 0
    ? movementRows.map((row: DmrMovementReportRow) => `
        <tr>
          <td>${renderVal(row.berShifting)}</td>
          <td>${renderVal(row.pilotName)}</td>
          <td>${renderVal(row.startedBothME)}</td>
          <td>${renderVal(row.cOff)}</td>
          <td>${renderVal(row.makeFastPushStart)}</td>
          <td>${renderVal(row.lineCastOffPushStop)}</td>
          <td>${renderVal(row.pilotRelease)}</td>
          <td>${renderVal(row.alongSideFinishedME)}</td>
          <td><strong>${row.totalRHrs || '-'}</strong></td>
          <td>${renderVal(row.otherTug)}</td>
          <td style="text-align:left;">${renderVal(row.remarks)}</td>
        </tr>
      `).join('')
    : `<tr><td colspan="11" style="text-align:center; color:#999;">No movement data</td></tr>`

  const fuelRowsHtml = fuelRows.length > 0
      ? fuelRows.map((row: any, idx: number) => `
          <tr>
            <td>${idx + 1}</td>
            <td style="text-align:left; font-weight:bold;">${renderVal(row.engine)}</td>
            <td>${renderVal(row.consumption)}</td>
            <td>${renderVal(row.lastRunningHours)}</td>
            <td>${renderVal(row.openingRunningHours)}</td>
            <td>${renderVal(row.closingRunningHours)}</td>
            <td>${renderVal(row.average)}</td>
          </tr>
        `).join('')
      : `<tr><td colspan="7" style="text-align:center; color:#999;">No fuel data recorded</td></tr>`

  const lubeRowsHtml = lubeRows.length > 0
    ? lubeRows.map((row: any, idx: number) => `
        <tr>
          <td>${idx + 1}</td>
          <td style="text-align:left; font-weight:bold;">${renderVal(row.grades || row.machinery)}</td>
          <td>${renderVal(row.openingStock)}</td>
          <td>${renderVal(row.received)}</td>
          <td>${renderVal(row.consumedByVessel)}</td>
          <td>${renderVal(row.closingStock)}</td>
        </tr>
      `).join('')
    : `<tr><td colspan="6" style="text-align:center; color:#999;">No lube oil data recorded</td></tr>`

  const fuelConsumptionRowsHtml = fuelConsumptionRows.length > 0
    ? fuelConsumptionRows.map((row: any) => `
        <tr>
          <td>${renderVal(row.date)}</td>
          <td>${renderVal(row.portMEOpening)}</td>
          <td>${renderVal(row.portMEClosing)}</td>
          <td>${renderVal(row.pmeRun)}</td>
          <td>${renderVal(row.stbdMEOpening)}</td>
          <td>${renderVal(row.stbdMEClosing)}</td>
          <td>${renderVal(row.smeRun)}</td>
          <td>${renderVal(row.fuelConsByMEs)}</td>
          <td>${renderVal(row.portGenRun)}</td>
          <td>${renderVal(row.stbdGenRun)}</td>
          <td>${renderVal(row.fuelConsByGens)}</td>
          <td>${renderVal(row.robOpeningLtr)}</td>
          <td>${renderVal(row.transfer)}</td>
          <td>${renderVal(row.received)}</td>
          <td>${renderVal(row.consumption)}</td>
          <td>${renderVal(row.robClosingLtr)}</td>
        </tr>
      `).join('')
    : `<tr><td colspan="16" style="text-align:center; color:#999;">No fuel consumption data recorded</td></tr>`

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Coastal Position Report - ${dmr.vesselName || 'Report'} - ${formatDateDisplay(dmr.reportDate)}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, sans-serif; padding: 20px; font-size: 11px; }
        h1 { font-size: 18px; margin-bottom: 5px; text-align: center; }
        h2 { font-size: 14px; margin-bottom: 15px; text-align: center; color: #555; }
        .info-row { display: flex; gap: 30px; margin-bottom: 15px; flex-wrap: wrap; }
        .info-item { }
        .info-label { font-weight: bold; color: #666; font-size: 10px; text-transform: uppercase; }
        .info-value { font-weight: bold; font-size: 12px; }
        table { width: 100%; border-collapse: collapse; margin-top: 10px; margin-bottom: 20px; }
        th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: center; font-size: 10px; }
        th { background: #f5f5f5; font-weight: bold; text-transform: uppercase; font-size: 9px; }
        tfoot td { background: #eaf3ff; font-weight: bold; color: #0056b3; }
        .separator { border-top: 1px solid #ddd; margin: 15px 0; }
        .section-title { text-align:left; margin-bottom: 8px; font-size: 14px; font-weight: bold; }
        .data-grid { display: grid; grid-template-columns: repeat(6, 1fr); gap: 10px; margin-bottom: 15px; }
        @media print { body { padding: 10px; } }
      </style>
    </head>
    <body>
      <h1>COASTAL POSITION REPORT</h1>
      <h2>${dmr.vesselName || '-'} — ${formatDateDisplay(dmr.reportDate)}</h2>
      
      <div class="info-row">
        <div class="info-item">
          <div class="info-label">Vessel</div>
          <div class="info-value">${dmr.vesselName || '-'}</div>
        </div>
        <div class="info-item">
          <div class="info-label">Report Date</div>
          <div class="info-value">${formatDateDisplay(dmr.reportDate)}</div>
        </div>
        <div class="info-item">
          <div class="info-label">Status</div>
          <div class="info-value">${dmr.status || '-'}</div>
        </div>
        <div class="info-item">
          <div class="info-label">Vessel Status</div>
          <div class="info-value">${dmr.vesselStatus || '-'}</div>
        </div>
          <div class="info-item">
            <div class="info-label">Off-Hire</div>
            <div class="info-value">${dmr.isOffHire ? 'Yes' : 'No'}</div>
          </div>
        <div class="info-item">
            <div class="info-label">Position / Location</div>
            <div class="info-value">${dmr.positionLocation || '-'}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Time</div>
            <div class="info-value">${dmr.positionTime || '-'}</div>
          </div>
      </div>

      <div class="separator"></div>

      <div class="section-title">Daily Fuel Data</div>
      <div class="data-grid">
        <div class="info-item"><div class="info-label">Opening Stock</div><div class="info-value">${renderVal(dmr.dfdOpeningStock)}</div></div>
        <div class="info-item"><div class="info-label">Total Consumption</div><div class="info-value">${renderVal(dmr.dfdTotalConsumption)}</div></div>
        <div class="info-item"><div class="info-label">Last Running Hrs</div><div class="info-value">${renderVal(dmr.dfdLastRunningHrs || dmr.lastRunningHrs)}</div></div>
        <div class="info-item"><div class="info-label">Total Running Hrs</div><div class="info-value">${renderVal(dmr.dfdTotalRunningHrs || dmr.totalRunningHrs)}</div></div>
        <div class="info-item"><div class="info-label">Bunker</div><div class="info-value">${renderVal(dmr.dfdBunker)}</div></div>
        <div class="info-item"><div class="info-label">Closing Stock</div><div class="info-value">${renderVal(dmr.dfdClosingStock)}</div></div>
      </div>

      <div class="section-title" style="font-size: 12px; margin-top: 10px;">Remarks</div>
      <div style="margin-bottom: 12px; white-space: pre-wrap;">${renderVal(dmr.dfdRemarks)}</div>

      <div class="section-title" style="font-size: 12px; margin-top: 10px;">Fuel Consumption Details</div>
      <table>
        <thead>
          <tr>
            <th style="width: 50px;">Sr</th>
            <th>Engine</th>
            <th>Consumption (KL)</th>
            <th>Last Running Hours</th>
            <th>Opening Running Hours</th>
            <th>Closing Running Hours</th>
            <th>Average</th>
          </tr>
        </thead>
        <tbody>
          ${fuelRowsHtml}
        </tbody>
      </table>

      <div class="section-title" style="font-size: 12px; margin-top: 10px;">Lube Oil Status</div>
      <table>
        <thead>
          <tr>
            <th style="width: 50px;">Sr. No</th>
            <th>Grades</th>
            <th>Opening Stock</th>
            <th>Received</th>
            <th>Consumed by Vessel</th>
            <th>Closing Stock</th>
          </tr>
        </thead>
        <tbody>
          ${lubeRowsHtml}
        </tbody>
      </table>

      <div class="separator"></div>

      <!-- <div class="section-title">Fuel Consumption Report</div>
      <table>
        <thead>
          <tr>
            <th rowspan="2">DATE</th>
            <th colspan="3">PORT MAIN ENGINE</th>
            <th colspan="3">STBD MAIN ENGINE</th>
            <th rowspan="2">FUEL CONS BY M.E'S (LTRS)</th>
            <th colspan="2">GENERATOR ENGINE</th>
            <th rowspan="2">FUEL CONS BY GEN'S (LTRS)</th>
            <th rowspan="2">ROB OPENING (LTRS)</th>
            <th rowspan="2">TRANSFER (LTRS)</th>
            <th rowspan="2">RECEIVED (LTRS)</th>
            <th rowspan="2">CONSUMPTION (LTRS)</th>
            <th rowspan="2">ROB CLOSING (LTRS)</th>
          </tr>
          <tr>
            <th>OPENING (HRS)</th>
            <th>CLOSING (HRS)</th>
            <th>RUN (HRS)</th>
            <th>OPENING (HRS)</th>
            <th>CLOSING (HRS)</th>
            <th>RUN (HRS)</th>
            <th>PORT (HRS)</th>
            <th>STBD (HRS)</th>
          </tr>
        </thead>
        <tbody>
          ${fuelConsumptionRowsHtml}
        </tbody>
        ${fuelConsumptionRows.length > 0 ? `
        <tfoot>
          <tr>
            <td colspan="3" style="text-align: right; padding-right: 15px;">TOTAL</td>
            <td>${renderFcrTotal(fcrTotals.pmeRun)}</td>
            <td colspan="2"></td>
            <td>${renderFcrTotal(fcrTotals.smeRun)}</td>
            <td>${renderFcrTotal(fcrTotals.fuelConsByMEs)}</td>
            <td>${renderFcrTotal(fcrTotals.portGenRun)}</td>
            <td>${renderFcrTotal(fcrTotals.stbdGenRun)}</td>
            <td></td>
            <td></td>
            <td>${renderFcrTotal(fcrTotals.transfer)}</td>
            <td>${renderFcrTotal(fcrTotals.received)}</td>
            <td>${renderFcrTotal(fcrTotals.consumption)}</td>
            <td></td>
          </tr>
        </tfoot>
        ` : ''}
      </table> -->

      <div class="separator"></div>

      <div class="section-title">Movement Report</div>
      <table>
        <thead>
          <tr>
            <th>BER/U.BER/SHIFTING</th>
            <th>PILOT NAME</th>
            <th>STARTED BOTH M.E.</th>
            <th>C/OFF</th>
            <th>MAKE FAST /PUSH START</th>
            <th>LINE CAST OFF/PUSH STOP</th>
            <th>PILOT RELEASE</th>
            <th>ALONG SIDE/ FINISHED M.E.</th>
            <th>TOTAL R/HRS</th>
            <th>OTHER TUG</th>
            <th>REMARKS</th>
          </tr>
        </thead>
        <tbody>
          ${movementRowsHtml}
        </tbody>
        ${movementRows.length > 0 ? `
        <tfoot>
          <tr>
            <td colspan="8" style="text-align: right; padding-right: 15px;">Total Hours</td>
            <td>${totalHrs.toFixed(2)}</td>
            <td colspan="2"></td>
          </tr>
        </tfoot>
        ` : ''}
      </table>
    </body>
    </html>
  `

  const container = document.createElement('div')
    container.innerHTML = html

    const opt = {
      margin:       [0.5, 0.5, 0.5, 0.5],
      filename:     `DMR_${dmr.vesselName || 'Report'}_${dmr.reportDate || 'unknown'}.pdf`,
      image:        { type: 'jpeg', quality: 0.98 },
      html2canvas:  { scale: 2 },
      jsPDF:        { unit: 'in', format: 'a4', orientation: 'landscape' }
    }

    // @ts-ignore
    import('html2pdf.js').then((html2pdfModule) => {
      const html2pdf = html2pdfModule.default || html2pdfModule
      html2pdf().set(opt).from(container).save()
    }).catch(err => {
      console.error('Failed to load html2pdf', err)
      toast.error('Failed to generate PDF')
    })
}

/* ──────────────────── Download Fuel Record ──────────────────── */
const downloadFuelRecordAsPdf = (dmrList: any[], vesselName: string, fromDate: string, toDate: string) => {
  if (dmrList.length === 0) {
    toast.error('No data available to download for the selected criteria.', { position: 'top-center' })
    return
  }

  // Sort by date ascending to find opening and closing correctly
  const sorted = [...dmrList].sort((a, b) => new Date(a.reportDate).getTime() - new Date(b.reportDate).getTime())
  const firstReport = sorted[0]
  const lastReport = sorted[sorted.length - 1]

  let totalConsumptionOverall = 0
  let totalBunker = 0
  let totalTransfer = 0 // Not apparently in model, keeping as 0
  
  let totalPmeSmeRun = 0
  let totalFuelConsMEs = 0
  let totalMeAverage = 0
  
  let totalGenRun = 0
  let totalFuelConsGens = 0
  let totalGenAverage = 0

  sorted.forEach(dmr => {
    totalBunker += parseFloat(dmr.dfdBunker) || 0

    const fuelRows = (dmr.fuelRows || []).filter((r: any) => r.engine && r.engine.trim() !== '')
    fuelRows.forEach((row: any) => {
      const consumption = parseFloat(row.consumption) || 0
      const openingRunningHours = parseFloat(row.openingRunningHours) || 0
      const average = parseFloat(row.average) || 0

      totalConsumptionOverall += consumption

      const engineName = (row.engine || '').trim().toUpperCase()
      if (engineName === 'M/E') {
        totalPmeSmeRun += openingRunningHours
        totalFuelConsMEs += consumption
        totalMeAverage += average
      } else if (engineName === 'A/E') {
        totalGenRun += openingRunningHours
        totalFuelConsGens += consumption
        totalGenAverage += average
      }
    })
  })

  const avgME = totalMeAverage > 0 ? totalMeAverage.toFixed(2) : '0.00'
  const avgGen = totalGenAverage > 0 ? totalGenAverage.toFixed(2) : '0.00'

  const openingDateStr = formatDateDisplay(firstReport.reportDate)
  const closingDateStr = formatDateDisplay(lastReport.reportDate)
  
  const formatMonthYear = (dStr: string) => {
    if (!dStr) return ''
    const d = new Date(dStr)
    return isNaN(d.getTime()) ? '' : d.toLocaleString('default', { month: 'short' }).toUpperCase() + ' ' + d.getFullYear()
  }

  const fromMonthStr = formatMonthYear(fromDate)
  const toMonthStr = formatMonthYear(toDate)

  let periodStr = ''
  if (fromMonthStr && toMonthStr) {
    periodStr = fromMonthStr === toMonthStr ? fromMonthStr : `${fromMonthStr} - ${toMonthStr}`
  } else {
    periodStr = fromMonthStr || toMonthStr || 'PERIOD'
  }

  const container = document.createElement('div')
  container.style.padding = '20px'
  container.style.fontFamily = 'Arial, sans-serif'
  container.style.fontSize = '12px'
  container.style.width = '100%'

  container.innerHTML = `
    <table style="width: 100%; border-collapse: collapse; border: 1px solid black; font-size: 12px;">
      <tr>
        <td colspan="4" style="background-color: #FFFF00; font-weight: bold; text-align: center; font-size: 14px; border: 1px solid black; padding: 6px;">
          FUEL RECORD - ${vesselName || 'ALL'} - ${periodStr}
        </td>
      </tr>
      <tr>
        <td style="text-align:center; font-weight:bold; border: 1px solid black; padding: 6px; width: 40px;">1</td>
        <td style="border: 1px solid black; padding: 6px;">OPENING ROB AS ON ${openingDateStr}</td>
        <td style="text-align: right; border: 1px solid black; padding: 6px; width: 80px;">${firstReport.dfdOpeningStock || '0'}</td>
        <td style="text-align: center; border: 1px solid black; padding: 6px; width: 80px;">LTRS</td>
      </tr>
      <tr>
        <td style="text-align:center; font-weight:bold; border: 1px solid black; padding: 6px;">2</td>
        <td style="border: 1px solid black; padding: 6px;">TOTAL CONSUMPTION DURING THE PERIOD</td>
        <td style="text-align: right; border: 1px solid black; padding: 6px;">${totalConsumptionOverall.toFixed(1)}</td>
        <td style="text-align: center; border: 1px solid black; padding: 6px;">LTRS</td>
      </tr>
      <tr>
        <td style="text-align:center; font-weight:bold; border: 1px solid black; padding: 6px;">3</td>
        <td style="border: 1px solid black; padding: 6px;">DIESEL RECEIVED DURING THE PERIOD</td>
        <td style="text-align: right; border: 1px solid black; padding: 6px;">${totalBunker.toFixed(1)}</td>
        <td style="text-align: center; border: 1px solid black; padding: 6px;">LTRS</td>
      </tr>
      <tr>
        <td style="text-align:center; font-weight:bold; border: 1px solid black; padding: 6px;">4</td>
        <td style="border: 1px solid black; padding: 6px;">DIESEL TRANSFERRED DURING THE PERIOD</td>
        <td style="text-align: right; border: 1px solid black; padding: 6px;">${totalTransfer.toFixed(1)}</td>
        <td style="text-align: center; border: 1px solid black; padding: 6px;">LTRS</td>
      </tr>
      <tr>
        <td style="text-align:center; font-weight:bold; border: 1px solid black; padding: 6px;">5</td>
        <td style="border: 1px solid black; padding: 6px;">CLOSING ROB ON ${closingDateStr}</td>
        <td style="text-align: right; border: 1px solid black; padding: 6px;">${lastReport.dfdClosingStock || '0'}</td>
        <td style="text-align: center; border: 1px solid black; padding: 6px;">LTRS</td>
      </tr>
      <tr>
        <td style="text-align:center; font-weight:bold; border: 1px solid black; padding: 6px;">6</td>
        <td style="border: 1px solid black; padding: 6px;">MAIN ENGINE RUN IN THE PERIOD (S.M.E.+P.M.E.=TOTAL HOURS RUN)</td>
        <td style="text-align: right; border: 1px solid black; padding: 6px;">${totalPmeSmeRun.toFixed(1)}</td>
        <td style="text-align: center; border: 1px solid black; padding: 6px;">HRS</td>
      </tr>
      <tr>
        <td style="text-align:center; font-weight:bold; border: 1px solid black; padding: 6px;">7</td>
        <td style="border: 1px solid black; padding: 6px;">FUEL CONSUMED BY M/Es</td>
        <td style="text-align: right; border: 1px solid black; padding: 6px;">${totalFuelConsMEs.toFixed(1)}</td>
        <td style="text-align: center; border: 1px solid black; padding: 6px;">LTRS</td>
      </tr>
      <tr style="background-color: #FFFF00; font-weight: bold;">
        <td style="text-align:center; border: 1px solid black; padding: 6px;">8</td>
        <td style="border: 1px solid black; padding: 6px;">AVERAGE FUEL CONSUMPTION OF MAIN ENGINE/HOUR</td>
        <td style="text-align: right; border: 1px solid black; padding: 6px;">${avgME}</td>
        <td style="text-align: center; border: 1px solid black; padding: 6px;">LTRS/HRS</td>
      </tr>
      <tr>
        <td style="text-align:center; font-weight:bold; border: 1px solid black; padding: 6px;">9</td>
        <td style="border: 1px solid black; padding: 6px;">AUX. ENGINE/GENERATOR RUN IN THE PERIOD</td>
        <td style="text-align: right; border: 1px solid black; padding: 6px;">${totalGenRun.toFixed(1)}</td>
        <td style="text-align: center; border: 1px solid black; padding: 6px;">HRS</td>
      </tr>
      <tr>
        <td style="text-align:center; font-weight:bold; border: 1px solid black; padding: 6px;">10</td>
        <td style="border: 1px solid black; padding: 6px;">FUEL CONSUMED BY AUX. ENGINE</td>
        <td style="text-align: right; border: 1px solid black; padding: 6px;">${totalFuelConsGens.toFixed(1)}</td>
        <td style="text-align: center; border: 1px solid black; padding: 6px;">LTRS</td>
      </tr>
      <tr style="background-color: #FFFF00; font-weight: bold;">
        <td style="text-align:center; border: 1px solid black; padding: 6px;">11</td>
        <td style="border: 1px solid black; padding: 6px;">AVERAGE FUEL CONSUMPTION OF A.E./GENSET/HOUR</td>
        <td style="text-align: right; border: 1px solid black; padding: 6px;">${avgGen}</td>
        <td style="text-align: center; border: 1px solid black; padding: 6px;">LTRS/HRS</td>
      </tr>
      <tr>
        <td style="text-align:center; font-weight:bold; border: 1px solid black; padding: 6px;">12</td>
        <td style="border: 1px solid black; padding: 6px;">DIESEL ROB OFF-HIRE COMMENCED</td>
        <td style="text-align: right; border: 1px solid black; padding: 6px;">0.0</td>
        <td style="text-align: center; border: 1px solid black; padding: 6px;">LTRS</td>
      </tr>
      <tr>
        <td style="text-align:center; font-weight:bold; border: 1px solid black; padding: 6px;">13</td>
        <td style="border: 1px solid black; padding: 6px;">DIESEL ROB ON-HIRE COMMENCED</td>
        <td style="text-align: right; border: 1px solid black; padding: 6px;">0.0</td>
        <td style="text-align: center; border: 1px solid black; padding: 6px;">LTRS</td>
      </tr>
      <tr>
        <td style="text-align:center; font-weight:bold; border: 1px solid black; padding: 6px;">14</td>
        <td style="border: 1px solid black; padding: 6px;">DIESEL CONSUMPTION DURING DOWNTIME /OFFHIRE</td>
        <td style="text-align: right; border: 1px solid black; padding: 6px;">0.0</td>
        <td style="text-align: center; border: 1px solid black; padding: 6px;">LTRS</td>
      </tr>
    </table>
  `

  const opt = {
    margin:       [0.5, 0.5, 0.5, 0.5],
    filename:     `Fuel_Record_${vesselName || 'Report'}_${periodStr.replace(/\s+/g, '_')}.pdf`,
    image:        { type: 'jpeg', quality: 0.98 },
    html2canvas:  { scale: 2 },
    jsPDF:        { unit: 'in', format: 'a4', orientation: 'portrait' }
  }

  // @ts-ignore
  import('html2pdf.js').then((html2pdfModule) => {
    const html2pdf = html2pdfModule.default || html2pdfModule
    html2pdf().set(opt).from(container).save()
  }).catch(err => {
    console.error('Failed to load html2pdf', err)
    toast.error('Failed to generate PDF')
  })
}

/* ──────────────────── Main Page Component ──────────────────── */
const DailyMovementReportPage: React.FC = () => {
  const { currentUser } = useAuth()
  const { opCan, permissionsLoaded } = useOperationsAccess()
  const { state: globalState, vessels: globalVessels } = useGlobalFilter()
  const canReadCoastal = opCan(OP.READ_COASTAL_POSITION_REPORT)
  const canCreateCoastal = opCan(OP.CREATE_COASTAL_POSITION_REPORT)
  const canDownloadCoastal = opCan(OP.DOWNLOAD_COASTAL_POSITION_REPORT)
  const currentVesselId = Number((currentUser as any)?.vessel?.id ?? 0)
  const isVesselUser = (currentUser as any)?.role?.id === 4
  const currentUserVesselName = (currentUser as any)?.vessel?.fleet_name || ''

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<TabKey>('vessel-status')

  const [dmrList, setDmrList] = useState<any[]>([])
  const [vesselOptions, setVesselOptions] = useState<string[]>([])
  const [vesselNameToId, setVesselNameToId] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(false)

  // View modal state
  const [viewDmr, setViewDmr] = useState<any>(null)
  const [isViewOpen, setIsViewOpen] = useState(false)

  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [filterVessel, setFilterVessel] = useState(isVesselUser ? currentUserVesselName : '')
  const [filterFromDate, setFilterFromDate] = useState('')
  const [filterToDate, setFilterToDate] = useState('')

  // Shared state for the entire DMR workflow
  const [dmrRecord, setDmrRecord] = useState<DailyMovementReportRecord>(blankDailyMovementReport())
  const [positionFields, setPositionFields] = useState(blankVesselStatusPositionFields)
  const [showErrors, setShowErrors] = useState(false)

  const selectedVesselName = isVesselUser
    ? currentUserVesselName
    : (globalState.vessel?.fleet_name || (globalState.vessel as any)?.vessel_name || (globalState.vessel as any)?.name || filterVessel || '')

  const previousFuelReport = getPreviousFuelReport(dmrList, dmrRecord.vesselName, dmrRecord.vesselId)



  const allowedVesselIds = useMemo(() => {
    if (!globalState.company) return null
    return new Set(globalVessels.map((v: any) => Number(v.id)))
  }, [globalState.company, globalVessels])

  const filteredDmrList = dmrList.filter((r) => {
    if (isVesselUser) {
      if (r.vesselId && r.vesselId !== currentVesselId) return false
      if (!r.vesselId && r.vesselName !== currentUserVesselName) return false
    }
    if (!isVesselUser && filterVessel) {
      const filterVesselId = vesselNameToId[filterVessel]
      if (filterVesselId && r.vesselId && r.vesselId !== filterVesselId) return false
      if ((!filterVesselId || !r.vesselId) && r.vesselName !== filterVessel) return false
    }

    // Global filter: vessel
    if (globalState.vessel?.id != null) {
      const rVId = r.vesselId ?? (r as any).vessel?.id
      if (rVId != null && String(rVId) !== String(globalState.vessel.id)) return false
    }

    // Global filter: company
    if (globalState.company && allowedVesselIds) {
      const rVId = r.vesselId ?? (r as any).vessel?.id
      if (rVId != null && allowedVesselIds.has(Number(rVId))) {
        // Matched via company vessel!
      } else {
        const rCgId = (r as any).companyGroupAdminId ?? (r as any).companyGroupId ?? (r as any).companyGroupAdmin?.id ?? (r as any).vessel?.companyGroupAdminId ?? (r as any).vessel?.companyGroupId ?? (r as any).vessel?.companyGroupAdmin?.id ?? (r as any).vessel?.cgaid?.id ?? (r as any).vessel?.cgaid
        const rCaId = (r as any).companyAdminId ?? (r as any).companyId ?? (r as any).companyAdmin?.id ?? (r as any).vessel?.companyAdminId ?? (r as any).vessel?.companyId ?? (r as any).vessel?.companyAdmin?.id
        if (globalState.company.type === 'company_group') {
          if (rCgId != null && Number(rCgId) !== globalState.company.id) return false
        } else {
          if (rCaId != null && Number(rCaId) !== globalState.company.id) return false
        }
      }
    }

    if (filterFromDate || filterToDate) {
      const rd = new Date(r.reportDate)
      rd.setHours(0, 0, 0, 0)

      if (filterFromDate) {
        const fd = new Date(filterFromDate)
        fd.setHours(0, 0, 0, 0)
        if (rd < fd) return false
      }
      if (filterToDate) {
        const td = new Date(filterToDate)
        td.setHours(0, 0, 0, 0)
        if (rd > td) return false
      }
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      const matchName = 'coastal position report'.includes(q)
      const matchVessel = (r.vesselName || '').toLowerCase().includes(q)
      const matchStatus = (r.status || '').toLowerCase().includes(q)
      const matchRank = (r.createdByRank || '').toLowerCase().includes(q)
      if (!matchName && !matchVessel && !matchStatus && !matchRank) return false
    }

    return true
  })

  const fetchDmrList = async () => {
    setLoading(true)
    try {
      // Load all DMRs so the previous report lookup can work for any selected vessel.
      const data = await getDmrList()
      setDmrList(data)
    } catch (err: any) {
      console.error('Failed to fetch DMR list', err)
      toast.error(getApiErrorMessage(err, 'Failed to fetch Coastal Position Reports.'))
    } finally {
      setLoading(false)
    }
  }

  const fetchVessels = async () => {
    try {
      const vessels = normalizeAccessibleVessels(await fetchAccessibleVesselsForCurrentUser())
      const names = vessels
        .map((v: any) => String(v?.fleet_name || '').trim())
        .filter((name) => name !== '')

      const uniqueNames = Array.from(new Set(names)).sort((a, b) => a.localeCompare(b))
      setVesselOptions(uniqueNames)

      const mapping: Record<string, number> = {}
      vessels.forEach((v: any) => {
        const name = String(v?.fleet_name || '').trim()
        if (name && v?.id !== undefined && v?.id !== null && mapping[name] === undefined) {
          mapping[name] = Number(v.id)
        }
      })
      setVesselNameToId(mapping)
    } catch (err: any) {
      console.error('Failed to fetch vessels', err)
      toast.error(getApiErrorMessage(err, 'Failed to fetch accessible vessels.'))
      setVesselOptions([])
      setVesselNameToId({})
    }
  }

  useEffect(() => {
    fetchDmrList()
    fetchVessels()
  }, [])

  const validateMandatoryForSubmit = () => {
    setShowErrors(true)
    
    if (!dmrRecord.vesselName) {
      toast.error('Please select vessel.', { position: 'top-center' })
      setActiveTab('vessel-status')
      return false
    }

    if (!dmrRecord.reportDate) {
      toast.error('Report Date is required.', { position: 'top-center' })
      setActiveTab('vessel-status')
      return false
    }

    if (!dmrRecord.vesselStatus) {
      toast.error('Vessel Status is required.', { position: 'top-center' })
      setActiveTab('vessel-status')
      return false
    }

    const latMissing = !positionFields.startLatDeg || !positionFields.startLatMin || !positionFields.startLatSec
    const lonMissing = !positionFields.startLonDeg || !positionFields.startLonMin || !positionFields.startLonSec
    if (latMissing || lonMissing) {
      toast.error('Please fill all required position latitude/longitude fields.', { position: 'top-center' })
      setActiveTab('vessel-status')
      return false
    }

    return true
  }

  const handleOpenModal = () => {
    setDmrRecord({
      ...blankDailyMovementReport(),
      vesselName: isVesselUser ? currentUserVesselName : '',
      vesselId: isVesselUser ? currentVesselId : undefined,
    })
    setPositionFields(blankVesselStatusPositionFields())
    setShowErrors(false)
    setActiveTab('vessel-status')
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const handleViewReport = (dmr: any) => {
    setViewDmr(dmr)
    setIsViewOpen(true)
  }

  const handleDownloadReport = (dmr: any) => {
    downloadDmrAsPdf(dmr)
    toast.info('Report downloaded!', { position: 'top-center', autoClose: 2000 })
  }

  const handleDownloadFuelRecord = () => {
    toast.dismiss()
    if (!selectedVesselName) {
      toast.error('Please select a vessel.', { position: 'top-center' })
      return
    }
    if (!filterFromDate && !filterToDate) {
      toast.error('Please select From Date and To Date.', { position: 'top-center' })
      return
    }
    if (!filterFromDate) {
      toast.error('Please select From Date.', { position: 'top-center' })
      return
    }
    if (!filterToDate) {
      toast.error('Please select To Date.', { position: 'top-center' })
      return
    }
    if (new Date(filterFromDate) > new Date(filterToDate)) {
      toast.error('From Date cannot be later than To Date.', { position: 'top-center' })
      return
    }
    downloadFuelRecordAsPdf(filteredDmrList, selectedVesselName, filterFromDate, filterToDate)
  }

  const currentTabIndex = TABS.findIndex((tab) => tab.key === activeTab)
  const isFirstTab = currentTabIndex === 0
  const isLastTab = currentTabIndex === TABS.length - 1

  const goToPrevTab = () => {
    if (isFirstTab) return
    setActiveTab(TABS[currentTabIndex - 1].key)
  }

  const goToNextTab = () => {
    if (isLastTab) return

    if (!validateMandatoryForSubmit()) {
      return
    }

    setActiveTab(TABS[currentTabIndex + 1].key)
  }

  const handleSubmitWorkflow = async () => {
    if (!validateMandatoryForSubmit()) return

    try {
      const payload = {
        ...dmrRecord,
        positionLat: positionFields.startLatDeg ? `${positionFields.startLatDeg} ${positionFields.startLatMin} ${positionFields.startLatSec} ${positionFields.startLatDir}` : '',
        positionLon: positionFields.startLonDeg ? `${positionFields.startLonDeg} ${positionFields.startLonMin} ${positionFields.startLonSec} ${positionFields.startLonDir}` : '',
        createdByRank: (currentUser as any)?.rank?.rank || 'Unknown',
        createdByUsername: (currentUser as any)?.roleEntityName || (currentUser as any)?.username || (currentUser as any)?.name || '',
        status: 'SUBMITTED'
      }
      await createDmr(payload)
      toast.success('Coastal Position Report submitted.', { position: 'top-center' })
      handleCloseModal()
      fetchDmrList()
    } catch (err: any) {
      console.error('Submit failed', err)
      toast.error(getApiErrorMessage(err, 'Failed to submit Coastal Position Report.'), { position: 'top-center' })
    }
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'vessel-status':
        return (
          <VesselStatus
            vesselOptions={vesselOptions}
            selectedVessel={dmrRecord.vesselName}
            onVesselChange={(v) => setDmrRecord({ ...dmrRecord, vesselName: v, vesselId: vesselNameToId[v] ?? dmrRecord.vesselId })}
            selectedStatus={dmrRecord.vesselStatus}
            onStatusChange={(s) => setDmrRecord({ ...dmrRecord, vesselStatus: s })}
            isOffHire={dmrRecord.isOffHire || false}
            onOffHireChange={(checked) => setDmrRecord({ ...dmrRecord, isOffHire: checked })}
            reportDate={dmrRecord.reportDate}
            onDateChange={(d) => setDmrRecord({ ...dmrRecord, reportDate: d })}
            positionLocation={dmrRecord.positionLocation}
            onPositionLocationChange={(l) => setDmrRecord({ ...dmrRecord, positionLocation: l })}
            positionTime={dmrRecord.positionTime || ''}
            onPositionTimeChange={(t) => setDmrRecord({ ...dmrRecord, positionTime: t })}
            positionFields={positionFields}
            onPositionFieldsChange={setPositionFields}
            showErrors={showErrors}
            isVesselUser={isVesselUser}
          />
        )
      case 'daily-fuel-data':
        return <DailyFuelData 
          selectedVessel={dmrRecord.vesselName} 
          reportDate={dmrRecord.reportDate} 
          dmrRecord={dmrRecord}
          setDmrRecord={setDmrRecord}
          previousReport={previousFuelReport}
        />
      // case 'fuel-consumption-report':
        // return <FuelConsumptionReport ... />
      case 'movement-report':
        return <MovementReport 
          selectedVessel={dmrRecord.vesselName} 
          reportDate={dmrRecord.reportDate} 
          dmrRecord={dmrRecord}
          setDmrRecord={setDmrRecord}
          dmrList={dmrList}
        />
      default:
        return null
    }
  }
  if (permissionsLoaded && !canReadCoastal) {
    return (
      <div className='card'>
        <div className='card-body text-center py-10 text-muted'>
          You do not have permission to view Coastal Position Reports (READ_COASTAL_POSITION_REPORT).
        </div>
      </div>
    )
  }

  return (
    <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
      {permissionsLoaded && !canReadCoastal ? (
        <div className='alert alert-warning m-5'>
          You do not have permission to view Coastal Position Reports (READ_COASTAL_POSITION_REPORT).
        </div>
      ) : (
      <>
      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid'>
          {/* Header Card */}
          <div className='card mb-5'>
            <div className='card-header border-0 d-flex justify-content-between align-items-center pt-5 pb-4'>
              <div className='card-title m-0'>
                <h3 className='fw-bold m-0'>Coastal Position Report</h3>
              </div>
              {!isModalOpen && (
                <button
                  type='button'
                  className='btn btn-sm btn-primary fw-bolder d-flex align-items-center gap-2 shadow-sm'
                  onClick={handleOpenModal}
                >
                  <KTSVG path='/media/icons/duotune/general/gen035.svg' className='svg-icon-3 m-0' />
                  Add Report
                </button>
              )}
            </div>
            {!isModalOpen && (
              <div className='card-body pt-0 pb-4'>
                <div className='d-flex align-items-center gap-4 flex-wrap'>
                  {/* Search */}
                  <div className='position-relative' style={{ width: '250px' }}>
                    <Search className='svg-icon-2 text-muted position-absolute top-50 start-0 translate-middle-y ms-4' style={{zIndex: 1, pointerEvents: 'none'}} />
                    <input
                      type='text'
                      className='form-control form-control-sm form-control-solid ps-12'
                      placeholder='Search Report'
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>



                  {/* Date Range */}
                  <div className='d-flex align-items-center gap-2'>
                    <label className='fw-bold text-muted'>From:</label>
                    <input
                      type='date'
                      className='form-control form-control-sm form-control-solid'
                      value={filterFromDate}
                      onChange={(e) => setFilterFromDate(e.target.value)}
                    />
                  </div>
                  <div className='d-flex align-items-center gap-2'>
                    <label className='fw-bold text-muted'>To:</label>
                    <input
                      type='date'
                      className='form-control form-control-sm form-control-solid'
                      value={filterToDate}
                      onChange={(e) => setFilterToDate(e.target.value)}
                    />
                  </div>

                  {canDownloadCoastal && (
                    <button
                      type='button'
                      className='btn btn-sm btn-light-success fw-bolder'
                      onClick={handleDownloadFuelRecord}
                      title='Download Fuel Record'
                    >
                      <i className='bi bi-file-earmark-pdf me-2'></i>
                      Download Fuel Record
                    </button>
                  )}
                  {/* Clear button */}
                  {(searchQuery || filterVessel || filterFromDate || filterToDate) && (
                    <button
                      className='btn btn-sm btn-light-danger fw-bolder'
                      onClick={() => {
                        setSearchQuery('')
                        setFilterVessel(isVesselUser ? currentUserVesselName : '')
                        setFilterFromDate('')
                        setFilterToDate('')
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Table List View */}
          {!isModalOpen && (
            <div className='card'>
              <div className='card-body py-3'>
                <div className='table-responsive'>
                  <table className='table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4'>
                    <thead>
                      <tr className='fw-bolder text-muted bg-light'>
                        <th className='min-w-150px rounded-start'>REPORT NAME</th>
                        {!isVesselUser && <th className='min-w-120px'>VESSEL NAME</th>}
                        <th className='min-w-150px'>REPORT DATE</th>
                        <th className='min-w-120px'>CREATED BY</th>
                        <th className='min-w-100px'>STATUS</th>
                        <th className='min-w-180px text-end rounded-end'>ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <TableSkeleton rows={8} cols={isVesselUser ? 5 : 6} />
                      ) : filteredDmrList.length === 0 ? (
                        <tr>
                          <td colSpan={isVesselUser ? 5 : 6} className='text-center py-10 text-muted'>
                            No Coastal Position Reports found matching the criteria.
                          </td>
                        </tr>
                      ) : (
                        filteredDmrList.map((r, i) => (
                          <tr key={i}>
                            <td className='text-dark fw-bolder text-hover-primary'>Coastal Position Report</td>
                            {!isVesselUser && <td className='fw-bold'>
                              {/* {r.vesselName || '-'} */}
                              <VesselLandingLink
                                vesselId={r.vesselId ?? null}
                                label={r.vesselName}
                                className='text-dark text-decoration-underline'
                              />
                              </td>}
                            <td>{formatDateDisplay(r.reportDate)}</td>
                            <td className='text-muted fw-bold'>
                              {(r.createdByRank && r.createdByRank !== 'Unknown' && r.createdByRank !== '-') 
                                ? r.createdByRank 
                                : (r.createdByUsername || '-')}
                            </td>
                            <td>
                              <span className={`badge badge-light-${r.status === 'SUBMITTED' ? 'success' : 'warning'}`}>
                                {r.status}
                              </span>
                            </td>
                            <td className='text-end'>
                              {canDownloadCoastal && (
                              <button
                                className='btn btn-sm btn-icon btn-light-success me-1'
                                title='Download Report'
                                onClick={() => handleDownloadReport(r)}
                              >
                                <i className='bi bi-download' />
                              </button>
                              )}                              
                              <button
                                className='btn btn-sm btn-icon btn-light-primary me-1'
                                title='View Report'
                                onClick={() => handleViewReport(r)}
                              >
                                <i className='bi bi-eye' />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div
          className='modal-overlay'
          onClick={handleCloseModal}
          style={{ padding: 0 }}
        >
          <div
            className='modal-content'
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '96vw',
              maxWidth: '96vw',
              height: '94vh',
              maxHeight: '94vh',
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div
              className='custom-modal-header d-flex justify-content-between align-items-center'
              style={{ flexShrink: 0 }}
            >
              <h5 className='m-0'>Coastal Position Report</h5>
              <button className='close-btn' onClick={handleCloseModal}>
                <KTSVG path='/media/map/x.svg' className='svg-icon-2x' />
              </button>
            </div>

            <ul
              className='nav nav-tabs mb-0 px-4 pt-2'
              style={{ flexWrap: 'wrap', flexShrink: 0 }}
            >
              {TABS.map((tab) => (
                <li className='nav-item' key={tab.key}>
                  <button
                    className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault()
                      if (tab.key !== 'vessel-status' && !validateMandatoryForSubmit()) {
                        return
                      }
                      setActiveTab(tab.key)
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>

            <div
              className='custom-modal-body'
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                padding: '0',
              }}
            >
              {renderTabContent()}
            </div>

            <div
              className='d-flex justify-content-end align-items-center gap-3 px-6 py-4 border-top bg-white'
              style={{ flexShrink: 0 }}
            >
              {!isFirstTab && (
                <button type='button' className='btn btn-light-primary fw-bolder' onClick={goToPrevTab}>
                  Back
                </button>
              )}

              {!isLastTab && (
                <button type='button' className='btn btn-primary fw-bolder' onClick={goToNextTab}>
                  Next
                </button>
              )}

              {isLastTab && (
                <button type='button' className='btn btn-success fw-bolder' onClick={handleSubmitWorkflow}>
                  Submit
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      <ViewDmrModal
        isOpen={isViewOpen}
        onClose={() => { setIsViewOpen(false); setViewDmr(null) }}
        dmr={viewDmr}
      />
      </>
      )}
      <ToastContainer position='top-center' autoClose={3000} newestOnTop closeOnClick draggable pauseOnHover style={{ top: '5rem' }} />
    </div>
  )
}

export default DailyMovementReportPage
